import {useEffect,useState} from 'react';
import {Box,Button,Typography} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {styled} from '@mui/material/styles';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext.js';
import {uploadEntityVideo,getEntityVideoUrl} from '../../integration/ObjectManager.js';

const VisuallyHiddenInput=styled('input')({
    clip:'rect(0 0 0 0)',
    clipPath:'inset(50%)',
    height:1,
    overflow:'hidden',
    position:'absolute',
    bottom:0,
    left:0,
    whiteSpace:'nowrap',
    width:1,
});

//Generic video field widget - works for any entity whose backend model
//declares the <fieldName>_object_key/_filename/_mime_type/_size/_uploaded_at
//column convention (see database_core.manager's generic video functions).
//The anchor field this component is bound to must be named "<prefix>_filename"
//(e.g. "video_filename") - the storage-field prefix ("video") is derived from
//it and used both as the API fieldName and to read the sibling video columns
//off the same row.
//Always shows the player when a video exists (visible to both editors and
//read-only viewers); the upload/replace control respects read_only.
//Local filename/preview state updates immediately on upload success, and
//refreshData() (threaded down from PageDetail, same as setFieldValue) is
//called afterward to refresh recordRules/methods - otherwise action buttons
//that depend on the video existing (e.g. Submit for Analysis) would stay
//stale until the user manually reloads the page.
const FieldVideo=({row,fieldName,entityType,application,recordRules,refreshData})=>{
    const fieldProp=row?.[fieldName];
    // Deliberately NOT gated by the page's readOnly/"Edit" toggle (unlike
    // evaluateReadOnly, which every other field goes through) - mirrors
    // ImageTab.js's upload button, which is gated purely by permission
    // (entityRules.can_create there, can_update_record + the field's own
    // computed read_only here), not by whether the record is in edit mode.
    // Attaching a file is independent of editing the record's other fields;
    // requiring an extra "Edit" click first is exactly the confusing detour
    // this avoids.
    const readOnlyEvaluated=!recordRules?.can_update_record||!!fieldProp?.read_only;
    const recordId=row?.id?.value;
    const videoFieldPrefix=fieldName?.replace(/_filename$/,'')||'video';
    const [localFilename,setLocalFilename]=useState(fieldProp?.value);
    const [uploading,setUploading]=useState(false);
    const [videoSrc,setVideoSrc]=useState(null);
    const {showSnackbar}=useSnackbar();
    const {showLoading,hideLoading}=useLoading();

    useEffect(()=>{
        setLocalFilename(fieldProp?.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[fieldProp?.value,recordId]);

    useEffect(()=>{
        let isSubscribed=true;
        if(recordId&&localFilename){
            getEntityVideoUrl(entityType,recordId,videoFieldPrefix).then((url)=>{
                if(isSubscribed) setVideoSrc(url);
            });
        }
        else{
            setVideoSrc(null);
        }
        return()=>{isSubscribed=false;};
    },[entityType,recordId,videoFieldPrefix,localFilename]);

    const handleUpload=async(event)=>{
        const file=event.target.files[0];
        event.target.value=null;
        if(!file||!recordId) return;
        if(!file.type.startsWith('video/')){
            showSnackbar(application.getLocale('ErrorMsgInvalidVideoType'),'error');
            return;
        }
        setUploading(true);
        showLoading();
        const formData=new FormData();
        formData.append('file',file);
        const data=await uploadEntityVideo(entityType,recordId,videoFieldPrefix,formData);
        setUploading(false);
        hideLoading();
        if(data.error){
            showSnackbar(data.error,'error');
        }
        else{
            showSnackbar(application.getLocale('SuccessMsgVideoUploaded'),'success');
            setLocalFilename(data.filename);
            // The upload happens through its own dedicated endpoint, entirely
            // outside the normal setFieldValue/upsertRecord flow, so recordRules
            // (and therefore methods() / the Submit button) is otherwise stale
            // until a manual refresh - refreshData() re-fetches the record so
            // Submit for Analysis appears immediately once a video exists.
            refreshData&&refreshData();
        }
    };

    // mb (sx) alone gets overridden here - this Box sits inside PageDetail's per-line Stack,
    // which applies its own margin to spread fields apart and wins the specificity/insertion-
    // order race against the sx-generated class. An inline style always beats a class-based
    // rule (short of !important elsewhere), so it's used here instead.
    return (
        <Box sx={{flex:1,minWidth:0}} style={{marginBottom:'10px'}}>
            {videoSrc?(
                // centered rather than left-aligned - a portrait/vertical
                // recording (e.g. filmed on a phone) is much narrower than
                // maxHeight:400 lets it get tall, so without this it visibly
                // hugs the left edge of the field instead of sitting in the
                // middle of the available width.
                <Box sx={{display:'flex',justifyContent:'center'}}>
                    <video controls style={{maxWidth:'100%',maxHeight:400}} src={videoSrc}>
                    </video>
                </Box>
            ):(
                <Typography variant="body2" color="text.secondary">
                    {recordId?application.getLocale('LabelNoVideoUploaded'):application.getLocale('LabelSaveBeforeUploadingVideo')}
                </Typography>
            )}
            {!readOnlyEvaluated&&recordId&&(
                <Box sx={{mt:1}}>
                    <Button component="label" variant="outlined" startIcon={<FileUploadIcon/>} disabled={uploading}>
                        {localFilename?application.getLocale('ButtonReplaceVideo'):application.getLocale('ButtonUploadVideo')}
                        <VisuallyHiddenInput type="file" accept="video/*" onChange={handleUpload}/>
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default FieldVideo;
